import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { MyInstituteRequest } from '../../../models/institute/myInstituteRequest';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { MyInstituteService } from '../../../services/institute/myinstitute/my-institute.service';
import { MessageService } from 'primeng/api';
import { Institute } from '../../../models/institute/randomInstituteResponse';
import { CourseLevelEnum, CourseLevelEnumDescriptions } from '../../../models/enums/courseLevelEnum';

@Component({
  selector: 'app-mentor-deadline-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, CalendarModule, DialogModule, TooltipModule],
  templateUrl: './mentor-deadline-dialog.component.html',
  styleUrl: './mentor-deadline-dialog.component.scss'
})
export class MentorDeadlineDialogComponent implements OnChanges {

  @Input() studentId!: string;
  @Input() institute!: Institute;
  @Input() currentDeadline?: string | null;
  @Input() visible: boolean = false;
  @Input() isMentorView: boolean = true;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() deadlineSaved = new EventEmitter<string | null>();

  selectedDate: Date | null = null;
  maxDate: Date | null = null;
  minDate: Date = new Date();
  isSaving: boolean = false;

  constructor(
    private myInstituteService: MyInstituteService,
    private messageService: MessageService
  ) { 
    this.minDate.setHours(0, 0, 0, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.visible) {
      this.initDialog();
    }
  }

  onShow() {
    this.initDialog();
  }

  private initDialog() {
    // Set current selection
    this.selectedDate = this.currentDeadline ? new Date(this.currentDeadline) : null;
   
    
    // Update minDate to now
    this.minDate = new Date();
    this.minDate.setHours(0, 0, 0, 0);

    // Calculate max date based on official deadline
    this.calculateMaxDate();
  }

  private calculateMaxDate() {
    this.maxDate = null;
    if (this.institute?.applicationDeadlineRegularDecision) {
      const deadlineStr = this.institute.applicationDeadlineRegularDecision;
      
      // Try parsing as is first (in case it has a year like "30 Apr 2026")
      let target = new Date(deadlineStr);
      let yearAppended = false;
      
      // If it doesn't have a year or parsing failed, try adding current year
      if (isNaN(target.getTime()) || target.getFullYear() < 2000) {
        const currentYear = new Date().getFullYear();
        target = new Date(`${deadlineStr}, ${currentYear}`);
        yearAppended = true;
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Only shift to next year if we artificially appended the year
      // AND the target date has already passed
      if (yearAppended && !isNaN(target.getTime()) && target < today) {
        const shiftedDate = new Date(target);
        shiftedDate.setFullYear(today.getFullYear() + 1);
        target = shiftedDate;
      }

      if (!isNaN(target.getTime())) {
        this.maxDate = target;
        
        // If the maxDate is in the past, adjust minDate so the user can still select a date
        if (this.maxDate < today) {
           this.minDate = new Date(this.maxDate);
           this.minDate.setFullYear(this.maxDate.getFullYear() - 1); // Allow picking dates up to a year prior
        }
        
        return;
      }
    }
    this.maxDate = null;
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  save(): void {
    this.isSaving = true;
    const date = this.selectedDate;
    const isoDate = date ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}` : null;

    // Build the full request body as required by PUT /api/myinstitute/{id}
    const request: MyInstituteRequest = {
      studentId: this.studentId,
      isAddedByMentor: this.isMentorView,
      instituteId: this.institute.id,
      courseId: this.institute.courseId ?? 0,
      inTakeYear: this.institute.inTakeYear ?? null,
      courseLevel: this.institute.courseLevel ?? undefined,
      mentorDeadline: isoDate
    };

    const saveObs = this.institute.myInstituteId
      ? this.myInstituteService.updateMyInstitute(request, this.institute.myInstituteId)
      : this.myInstituteService.addMyInstituteByRequestModel(request);

    saveObs.subscribe({
      next: () => {
        this.isSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Successfully Added Mentor Deadline for student' });
        this.deadlineSaved.emit(isoDate);
        this.close();
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error updating deadline:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update deadline' });
      }
    });
  }


  getCourseLevelLabel(level: any): string | null {
    if (level === null || level === undefined || level === '') return null;
    return CourseLevelEnumDescriptions[Number(level) as CourseLevelEnum] || null;
  }
}
